import React, { useEffect, useState } from 'react';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { authService } from '../../lib/supabase';
import { createLogger } from '../../lib/logger';

const log = createLogger('auth');

interface OAuthCallbackProps {
  onSuccess: () => void;
  onError: (error: string) => void;
}

export const OAuthCallback: React.FC<OAuthCallbackProps> = ({ onSuccess, onError }) => {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('正在處理您的登入...');
  const [processed, setProcessed] = useState(false);

  useEffect(() => {
    const handleCallback = async () => {
      if (processed) {
        log.debug('OAuthCallback: 已經處理過，跳過重複處理');
        return;
      }
      
      setProcessed(true);
      try {
        log.debug('OAuthCallback: 開始處理 OAuth 回調');
        log.debug('OAuthCallback: 當前 URL:', window.location.href);
        log.debug('OAuthCallback: URL hash:', window.location.hash);
        log.debug('OAuthCallback: URL search:', window.location.search);
        
        const { user, error } = await authService.handleOAuthCallback();

        log.debug('OAuthCallback: 回調結果:', { user: user?.id, error: error?.message });

        if (error) {
          log.error('OAuthCallback: 登入錯誤:', error);
          setStatus('error');
          setMessage(`登入失敗：${error.message}`);
          onError(error.message || '登入失敗');
          return;
        }

        if (user) {
          log.info('OAuthCallback: 登入成功，用戶 ID:', user.id);
          setStatus('success');
          setMessage('登入成功！正在重新導向...');
          
          // 延遲一下讓用戶看到成功訊息
          setTimeout(() => {
            log.debug('OAuthCallback: 準備重新導向到首頁');
            onSuccess();
          }, 1500);
        } else {
          log.warn('OAuthCallback: 未能獲取用戶資訊');
          setStatus('error');
          setMessage('未能獲取用戶資訊');
          onError('登入失敗');
        }
      } catch (err) {
        log.error('OAuthCallback: 處理登入時發生未預期錯誤:', err);
        setStatus('error');
        setMessage('處理登入時發生錯誤');
        onError('登入處理失敗');
      }
    };

    handleCallback();
  }, [onSuccess, onError]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-rose-50 to-amber-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-xl shadow-lg shadow-wedding-pink/20 p-8 text-center">
          {status === 'loading' && (
            <>
              <Loader2 className="w-12 h-12 text-wedding-action mx-auto mb-4 animate-spin" />
              <h2 className="text-xl font-semibold text-wedding-text-title mb-2">處理中...</h2>
              <p className="text-wedding-text-body">{message}</p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-wedding-text-title mb-2">登入成功！</h2>
              <p className="text-wedding-text-body">{message}</p>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-wedding-text-title mb-2">登入失敗</h2>
              <p className="text-wedding-text-body mb-4">{message}</p>
              <button
                onClick={() => window.location.href = '/'}
                className="bg-wedding-action text-white px-6 py-2 rounded-md hover:bg-wedding-action-hover transition-colors"
              >
                返回首頁
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};